import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, Users, CreditCard, DollarSign, FileText, LogOut, Dumbbell, Menu, X, UserCog, Warehouse } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';

const Layout = ({ user, onLogout }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const menuItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/products', label: 'Productos', icon: Package },
    { path: '/sales', label: 'Ventas', icon: ShoppingCart },
    { path: '/subscriptions', label: 'Suscripciones', icon: CreditCard },
    { path: '/clients', label: 'Clientes', icon: Users },
    { path: '/expenses', label: 'Gastos', icon: DollarSign },
    { path: '/reports', label: 'Informes', icon: FileText },
  ];

  // Add admin-only menu items
  if (user?.role === 'admin') {
    menuItems.push({ path: '/inventory', label: 'Inventario', icon: Warehouse });
    menuItems.push({ path: '/sales-history', label: 'Historial de Ventas', icon: FileText });
    menuItems.push({ path: '/users', label: 'Usuarios', icon: UserCog });
  }

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className="layout">
      {/* Mobile Menu Button */}
      <button 
        className="mobile-menu-btn"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        data-testid="mobile-menu-btn"
        aria-label="Toggle menu"
      >
        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={closeSidebar}
          data-testid="sidebar-overlay"
        />
      )}

      <aside className={`sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Dumbbell size={32} strokeWidth={2.5} />
          </div>
          <h1 className="sidebar-title">GymHub</h1>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={closeSidebar}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.full_name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <p className="user-name">{user?.full_name}</p>
              <p className="user-email">{user?.username}</p>
              <p className="user-role">{user?.role === 'admin' ? 'Administrador' : 'Vendedor'}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={onLogout} 
            className="logout-btn"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
          </Button>
        </div>
      </aside>

      <main className="main-content">
        <Outlet />
      </main>

      <Toaster position="top-right" />

      <style jsx>{`
        .layout {
          display: flex;
          min-height: 100vh;
          background: #f8fafc;
          position: relative;
        }

        .mobile-menu-btn {
          display: none;
          position: fixed;
          top: 1rem;
          left: 1rem;
          z-index: 60;
          width: 48px;
          height: 48px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          align-items: center;
          justify-content: center;
          color: #1e293b;
          cursor: pointer;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: all 0.2s;
        }

        .mobile-menu-btn:hover {
          background: #f8fafc;
        }

        .mobile-menu-btn:active {
          transform: scale(0.95);
        }

        .sidebar-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 40;
          backdrop-filter: blur(2px);
        }

        .sidebar {
          width: 280px;
          background: white;
          border-right: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          position: fixed;
          height: 100vh;
          z-index: 50;
          transition: transform 0.3s ease-in-out;
        }

        .sidebar-header {
          padding: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .sidebar-logo {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .sidebar-title {
          font-size: 1.5rem;
          font-weight: 800;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .sidebar-nav {
          flex: 1;
          padding: 1.5rem 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          overflow-y: auto;
        }

        .nav-link {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          border-radius: 10px;
          color: #64748b;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.9375rem;
          transition: all 0.2s;
        }

        .nav-link:hover {
          background: #f1f5f9;
          color: #10b981;
        }

        .nav-link.active {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .sidebar-footer {
          padding: 1rem;
          border-top: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
          min-width: 0;
        }

        .user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1rem;
          flex-shrink: 0;
        }

        .user-details {
          flex: 1;
          min-width: 0;
        }

        .user-name {
          font-weight: 600;
          font-size: 0.875rem;
          color: #1e293b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-email {
          font-size: 0.75rem;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-role {
          font-size: 0.7rem;
          color: #10b981;
          font-weight: 600;
          margin-top: 0.125rem;
        }

        .logout-btn {
          color: #64748b;
          padding: 0.5rem;
          height: auto;
          flex-shrink: 0;
        }

        .logout-btn:hover {
          background: #fee2e2;
          color: #ef4444;
        }

        .main-content {
          flex: 1;
          margin-left: 280px;
          padding: 2rem;
          min-height: 100vh;
        }

        @media (max-width: 1024px) {
          .sidebar {
            width: 240px;
          }

          .main-content {
            margin-left: 240px;
          }
        }

        @media (max-width: 1170px) {
          .mobile-menu-btn {
            display: flex;
          }

          .sidebar {
            transform: translateX(-100%);
          }

          .sidebar.sidebar-open {
            transform: translateX(0);
          }

          .sidebar-overlay {
            display: block;
          }

          .main-content {
            margin-left: 0;
            padding-top: 5rem;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 5rem 1rem 1rem 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Layout;
